import { useEffect, useState } from "react";
import { useSettings } from "@/lib/settings";
import api from "@/lib/api";
import { Sparkles, Target, Award, PlayCircle, ChevronRight, Rocket, Shield, Zap, Brain } from "lucide-react";
import { Link } from "react-router-dom";
import Counter from "@/components/Counter";

function toEmbedUrl(url) {
  if (!url) return "";
  if (url.includes("youtu.be/")) return `https://www.youtube.com/embed/${url.split("youtu.be/")[1].split(/[?&]/)[0]}`;
  if (url.includes("youtube.com/watch")) { try { return `https://www.youtube.com/embed/${new URL(url).searchParams.get("v")}`; } catch { return url; } }
  return url;
}

export default function AboutPage() {
  const { settings } = useSettings();
  const [trainers, setTrainers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [courses, setCourses] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/trainers").then(({ data }) => setTrainers(data));
    api.get("/testimonials").then(({ data }) => setTestimonials(data));
    api.get("/courses").then(({ data }) => setCourses(data));
    api.get("/gallery").then(({ data }) => setGallery(data));
  }, []);

  if (!settings) return <div className="min-h-[60vh] flex items-center justify-center">Loading...</div>;
  const a = settings.about_page || {};
  const featSlugs = a.featured_courses_slugs || [];
  const featCourses = featSlugs.length > 0
    ? featSlugs.map((s) => courses.find((c) => c.slug === s)).filter(Boolean)
    : courses.slice(0, 3);
  const partnersVisible = (settings.hiring_partners || []).filter((p) => p.visible !== false);
  const partnersCount = a.hiring_partners_auto_count ? `${partnersVisible.length}+` : (a.hiring_partners_manual_count || "50+");

  return (
    <div className="pb-24" data-testid="about-page">
      {/* Banner */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        {a.banner_image && (
          <img src={a.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/95 via-neutral-950/80 to-transparent"></div>
        <div className="container-eq relative text-white">
          <div className="max-w-2xl">
            <div className="label-eq text-sky-400 mb-3">{a.banner_subtitle}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter" data-testid="about-title">{a.banner_title}</h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 container-eq grid lg:grid-cols-5 gap-10 items-center" data-testid="about-intro">
        <div className="lg:col-span-3">
          <div className="label-eq text-sky-500 mb-3">Introduction</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight leading-tight">{a.intro_heading}</h2>
          <p className="mt-5 text-neutral-600 leading-relaxed text-lg">{a.intro_body}</p>
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          {(a.achievements || []).map((s, i) => (
            <div key={i} className="p-6 card-eq text-center" data-testid={`about-stat-${i}`}>
              <div className="text-4xl font-display font-black text-sky-500"><Counter target={s.metric} /></div>
              <div className="text-xs text-neutral-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story / Vision / Mission cards */}
      <section className="py-20 bg-white" data-testid="about-story-vision-mission">
        <div className="container-eq grid md:grid-cols-3 gap-6">
          {[
            { title: a.story_heading, body: a.story_body, icon: Rocket, tint: "sky" },
            { title: a.vision_heading, body: a.vision_body, icon: Target, tint: "orange" },
            { title: a.mission_heading, body: a.mission_body, icon: Sparkles, tint: "emerald" },
          ].map((c, i) => (
            <div key={i} className="p-8 card-eq relative overflow-hidden">
              <div className={`w-12 h-12 rounded-xl bg-neutral-950 flex items-center justify-center mb-4`}>
                <c.icon className="w-6 h-6 text-sky-400" />
              </div>
              <h3 className="font-display font-bold text-xl">{c.title}</h3>
              <p className="mt-3 text-neutral-600 text-sm leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem */}
      <section className="py-20 container-eq" data-testid="about-ecosystem">
        <div className="max-w-3xl">
          <div className="label-eq text-sky-500 mb-3">Ecosystem</div>
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{a.ecosystem_heading}</h2>
          <p className="mt-4 text-neutral-600 leading-relaxed">{a.ecosystem_body}</p>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-sky-50" data-testid="about-certifications">
        <div className="container-eq grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="w-12 h-12 rounded-xl bg-neutral-950 flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-sky-400" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{a.certifications_heading}</h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">{a.certifications_body}</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {partnersVisible.slice(0, 9).map((p, i) => (
              <div key={p.name || i} className="aspect-square rounded-2xl bg-white border border-neutral-200 flex items-center justify-center font-display font-bold text-neutral-400 overflow-hidden" data-testid={`about-partner-${i}`}>
                {p.logo ? <img src={p.logo} alt={p.name} className="max-w-[70%] max-h-[70%] object-contain" /> : p.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools stack */}
      <section className="py-20 container-eq" data-testid="about-tools">
        <div className="max-w-2xl mb-10">
          <div className="label-eq text-sky-500 mb-3">Curriculum tools</div>
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{a.tools_heading}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { label: "AI & Automation", items: a.tools_ai || [], icon: Brain },
            { label: "Ads & Performance", items: a.tools_ads || [], icon: Zap },
            { label: "SEO & Analytics", items: a.tools_seo || [], icon: Target },
          ].map((g, i) => (
            <div key={i} className="p-6 card-eq">
              <div className="flex items-center gap-2 mb-4">
                <g.icon className="w-5 h-5 text-sky-500" />
                <div className="font-display font-bold">{g.label}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {g.items.map((t, j) => <span key={j} className="text-xs px-3 py-1.5 rounded-full bg-neutral-100 border border-neutral-200">{t}</span>)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Programs */}
      {a.featured_courses_enabled !== false && (
      <section className="py-20 bg-neutral-950 text-white" data-testid="about-programs">
        <div className="container-eq">
          <div className="max-w-2xl mb-10">
            <div className="label-eq text-sky-400 mb-3">Flagship Programs</div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{a.featured_courses_heading || a.programs_heading || "Our Flagship Programs"}</h2>
          </div>
          <div className={a.featured_courses_layout === "carousel" ? "flex gap-6 overflow-x-auto pb-4" : "grid md:grid-cols-3 gap-6"}>
            {featCourses.map((c) => (
              <Link key={c.id} to={`/courses/${c.slug}`} className={`p-6 rounded-2xl bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 group transition-colors ${a.featured_courses_layout === "carousel" ? "min-w-[300px]" : ""}`} data-testid={`about-featured-${c.slug}`}>
                <div className="text-xs uppercase tracking-widest text-sky-400 font-semibold">{c.category}</div>
                <h3 className="font-display font-bold text-xl mt-2">{c.title}</h3>
                <p className="text-sm text-neutral-400 mt-2 line-clamp-2">{c.subtitle}</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  {a.featured_courses_show_duration !== false && <span className="text-neutral-500">{c.duration}</span>}
                  {a.featured_courses_show_price !== false && <span className="font-bold">{c.fees}</span>}
                </div>
                {a.featured_courses_show_trainer && c.trainer && <div className="mt-1 text-xs text-neutral-500">by {c.trainer}</div>}
                <div className="mt-4 flex flex-wrap gap-2">
                  {a.featured_courses_show_enroll !== false && <span className="inline-flex items-center gap-1 text-sm text-sky-400 group-hover:gap-2 transition-all">Enrol Now <ChevronRight className="w-4 h-4" /></span>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>)}

      {/* Founder */}
      <section className="py-20 container-eq" data-testid="about-founder">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {a.founder_image && (
            <div className="relative">
              <img src={a.founder_image} alt={a.founder_name} className="rounded-3xl w-full h-[500px] object-cover" />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl max-w-xs border border-neutral-100">
                <div className="font-display font-bold">{a.founder_name}</div>
                <div className="text-xs text-sky-500 font-semibold uppercase tracking-widest">{a.founder_role}</div>
              </div>
            </div>
          )}
          <div>
            <div className="label-eq text-sky-500 mb-3">{a.founder_heading}</div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{a.founder_name}</h2>
            <div className="text-sm text-neutral-500 mt-1">{a.founder_role}</div>
            <p className="mt-5 text-neutral-600 leading-relaxed text-lg italic">"{a.founder_message}"</p>
          </div>
        </div>
      </section>

      {/* Team */}
      {trainers.length > 0 && (
        <section className="py-20 bg-neutral-50" data-testid="about-team">
          <div className="container-eq">
            <div className="max-w-2xl mb-10">
              <div className="label-eq text-sky-500 mb-3">Trainers</div>
              <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Meet the mentors behind every graduate</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {trainers.slice(0, 3).map((t) => (
                <div key={t.id} className="p-6 card-eq">
                  <div className="aspect-square rounded-xl overflow-hidden bg-neutral-100 mb-4">
                    {t.image && <img src={t.image} alt={t.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="font-display font-bold">{t.name}</div>
                  <div className="text-sm text-sky-600 font-semibold">{t.role}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link to="/trainers" className="btn-outline-eq text-sm">View all trainers</Link>
            </div>
          </div>
        </section>
      )}

      {/* Gallery preview */}
      {gallery.length > 0 && (
        <section className="py-20 container-eq" data-testid="about-gallery">
          <div className="max-w-2xl mb-10">
            <div className="label-eq text-sky-500 mb-3">Gallery</div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Life at EQtech Academy</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gallery.slice(0, 8).map((g) => (
              <div key={g.id} className="aspect-square rounded-2xl overflow-hidden bg-neutral-100">
                <img src={g.image} alt={g.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Video */}
      {a.video_youtube_url && (
        <section className="py-20 bg-neutral-950" data-testid="about-video">
          <div className="container-eq">
            <div className="max-w-2xl mb-10 text-white">
              <div className="label-eq text-sky-400 mb-3">Watch</div>
              <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{a.video_heading}</h2>
            </div>
            <div className="rounded-3xl overflow-hidden aspect-video max-w-4xl mx-auto">
              <iframe src={toEmbedUrl(a.video_youtube_url)} title="About EQtech" className="w-full h-full" allowFullScreen />
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 container-eq" data-testid="about-testimonials">
          <div className="max-w-2xl mb-10">
            <div className="label-eq text-sky-500 mb-3">Success Stories</div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Student voices</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t) => (
              <div key={t.id} className="p-6 card-eq">
                <p className="text-sm text-neutral-700 italic">"{t.review}"</p>
                <div className="mt-4 flex items-center gap-3 pt-4 border-t border-neutral-100">
                  {t.image && <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" />}
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-neutral-500">{t.course}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-500 to-sky-600 text-white" data-testid="about-cta">
        <div className="container-eq text-center max-w-3xl">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tight">{a.cta_heading}</h2>
          <div className="mt-8">
            {a.cta_button_link?.startsWith("http")
              ? <a href={a.cta_button_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white text-neutral-950 px-8 py-4 rounded-full font-bold hover:scale-105 transition-transform" data-testid="about-cta-button">{a.cta_button_text} <ChevronRight className="w-4 h-4" /></a>
              : <Link to={a.cta_button_link || "/contact"} className="inline-flex items-center gap-2 bg-white text-neutral-950 px-8 py-4 rounded-full font-bold hover:scale-105 transition-transform" data-testid="about-cta-button">{a.cta_button_text} <ChevronRight className="w-4 h-4" /></Link>}
          </div>
        </div>
      </section>
    </div>
  );
}
